/*
 * -----------------------------------------------------------------------
 *  <copyright file="admin-risks.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react';
import { Tabs } from '@mui/material';
import Tab from '@mui/material/Tab';
import { pick } from 'lodash';
import AddItemsBtns from '@components/admin/add-items-btns';
import AddLibraryControl from '@components/admin/add-library-control';
import AddLibraryControlsRisk from '@components/admin/add-library-controls-risk';
import AddFunctionalDataReviewDialog from '@components/admin/add-library-functional-data-review-dialog';
import AddLibraryRisk from '@components/admin/add-library-risk';
import AddLibraryRisksControl from '@components/admin/add-library-risks-control';
import AdminControlsGrid from '@components/admin/admin-controls-grid';
import AdminRisksGrid from '@components/admin/admin-risks-grid';
import RisksAndControlsImportDialog from '@components/admin/risks-and-controls-import-dialog';
import RisksReorderingGrid from '@components/admin/risks-reordering-grid';
import UnassignLibraryControl from '@components/admin/unassign-library-control';
import ActionDialog from '@components/core/action-dialog';
import { ExtraMenuItemPosition } from '@components/core/grid-header/grid-menu/grid-menu.interface';
import Loading from '@components/core/loading';
import PageTitle from '@components/core/page-title/page-title';
import Error from '@components/error/error';
import {
    GET_ADMIN_RISK_REORDERING_GRID_COLUMNS,
    updateRisksReorderingGridData,
} from '@pages/admin/admin-risks/admin-risks.helper';
import {
    LibraryControlSystemUpdate,
    LibraryControlOrgUpdate,
} from '@pages/admin/admin-risks/library-control-update.interface';
import { LibraryRiskSystemUpdate, LibraryRiskOrgUpdate } from '@pages/admin/admin-risks/library-risk-update.interface';
import { LibraryControlFieldsExtended } from '@interfaces/library-control.interface';
import { LibraryRiskFieldsExtended } from '@interfaces/library-risk.interface';
import { NamedItemType } from 'types/graphql-global-types';
import { useTranslation, useBoolean, useAuthorization, useLocalState } from '@hooks/index';
import Constants from '@utils/constants';
import { convertReviewFrequency, convertDateToDays } from '@utils/convertor';
import NotificationService from '@utils/notification';
import { AllRoutes } from '@utils/router/all-routes';
import { GET_NAMED_ITEMS_BY_TYPE } from '@queries/common';
import { getNamedItemsByType, getNamedItemsByTypeVariables } from '@queries/generated/common.generated';
import {
    getLibraryRisks_OpraQueries_libraryRisks_LibraryRisk,
    getLibraryRisks_OpraQueries_libraryRisks_LibraryRisk_libraryControls_LibraryControl,
    libraryFunctionalDataReviews_OpraQueries_libraryFunctionalDataReviews_LibraryFunctionalDataReview,
    getLibraryControls,
    getLibraryControlsVariables,
    getLibraryRisks,
    getLibraryRisksVariables,
} from '@queries/generated/legacy-risk-assessment.generated';
import {
    GET_LIBRARY_RISKS,
    UPDATE_LIBRARY_RISK,
    UPDATE_LIBRARY_RISKS,
    UPDATE_LIBRARY_CONTROLS,
    GET_LIBRARY_CONTROLS,
    UPDATE_LIBRARY_CONTROL,
    DELETE_LIBRARY_CONTROL,
    DELETE_LIBRARY_RISK,
    GET_LIBRARY_FUNCTIONAL_DATA_REVIEWS,
} from '@queries/legacy-risk-assessment';

import '../admin.scss';
import Button from '@components/core/buttons';

import FunctionalDataReviewGrid from '@components/admin/library-functional-data-review-grid';
import { notEmpty } from '@utils/utility-helpers';

const AdminRisks = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const { state } = useLocalState();
    const user = useAuthorization();

    const [active, setActive] = useState(0);
    const [processedLibraryRisks, setProcessedLibraryRisks] = useState<LibraryRiskFieldsExtended[]>([]);
    const [processedLibraryControls, setProcessedLibraryControls] = useState<LibraryControlFieldsExtended[]>([]);
    const [currentRisk, setCurrentRisk] = useState<LibraryRiskFieldsExtended | null>(null);
    const [organisationId, setOrganisationId] = useState<string>('');
    const [currentControl, setCurrentControl] = useState<LibraryControlFieldsExtended | null>(null);
    const [currentFunctionalDataReview, setCurrentFunctionalDataReview] =
        useState<libraryFunctionalDataReviews_OpraQueries_libraryFunctionalDataReviews_LibraryFunctionalDataReview | null>(
            null,
        );
    const importDialogOpen = useBoolean(false);
    const showAddLibraryRiskModal = useBoolean(false);
    const showAssignControls = useBoolean(false);
    const showAddLibraryControl = useBoolean(false);
    const showAssignRisks = useBoolean(false);
    const showAddRisks = useBoolean(false);
    const showAddControls = useBoolean(false);
    const showDeleteItem = useBoolean(false);
    const showUnassignControlFromRisk = useBoolean(false);
    const showUnassignRiskFromControl = useBoolean(false);
    const showEditRiskOrderDialog = useBoolean(false);
    const loadingAssignControls = useBoolean(false);
    const onLoadingAssignRisks = useBoolean(false);
    const showReorderGrid = useBoolean(false);
    const showAddFunctionalDataReviewDialog = useBoolean(false);

    const [saveLibraryRisk] = useMutation(UPDATE_LIBRARY_RISK);
    const [deleteLibraryRisk] = useMutation(DELETE_LIBRARY_RISK);
    const [saveLibraryControl] = useMutation(UPDATE_LIBRARY_CONTROL);
    const [deleteLibraryControl] = useMutation(DELETE_LIBRARY_CONTROL);
    const [saveLibraryControls] = useMutation(UPDATE_LIBRARY_CONTROLS);
    const [saveLibraryRisks] = useMutation(UPDATE_LIBRARY_RISKS);

    const canEdit = location.pathname.match(AllRoutes.path('org-risk-library'))
        ? user?.canWriteSystemAdminLevel()
        : user.canWriteSystemAdminLevel();
    const isSystemLevel = organisationId === Constants.ObjectId.Empty;
    const libraryControlUpdate = isSystemLevel ? LibraryControlSystemUpdate : LibraryControlOrgUpdate;
    const libraryRiskUpdate = isSystemLevel ? LibraryRiskSystemUpdate : LibraryRiskOrgUpdate;
    const isRiskTab = active === 0;

    const [loadSystemRisks, { data: systemLibraryRisksData, loading: loadingLibraryRisks }] = useLazyQuery<
        getLibraryRisks,
        getLibraryRisksVariables
    >(GET_LIBRARY_RISKS);
    const [loadSystemControls, { data: systemLibraryControlsData, loading: loadingLibraryControls }] = useLazyQuery<
        getLibraryControls,
        getLibraryControlsVariables
    >(GET_LIBRARY_CONTROLS);
    const [
        loadLibraryFunctionalDataReviews,
        { data: LibraryFunctionalDataReviewsData, loading: loadingLibraryFunctionalDataReviews },
    ] = useLazyQuery(GET_LIBRARY_FUNCTIONAL_DATA_REVIEWS);

    const {
        loading: libraryRisksLoading,
        error: libraryRisksError,
        data: libraryRisksData,
    } = useQuery<getLibraryRisks, getLibraryRisksVariables>(GET_LIBRARY_RISKS, {
        variables: {
            organisationId,
            includeSystemRisks: isSystemLevel,
        },
    });

    const libraryRisks = libraryRisksData?.libraryRisks;

    const { loading: libraryControlsLoading, data: libraryControlsData } = useQuery<
        getLibraryControls,
        getLibraryControlsVariables
    >(GET_LIBRARY_CONTROLS, {
        variables: {
            organisationId,
            includeSystemControls: isSystemLevel,
        },
    });

    const libraryControls = libraryControlsData?.libraryControls;

    const [loadRiskLibraryCategories, { data: riskLibraryCategoriesData }] = useLazyQuery<
        getNamedItemsByType,
        getNamedItemsByTypeVariables
    >(GET_NAMED_ITEMS_BY_TYPE);

    const riskLibraryCategories = riskLibraryCategoriesData?.namedItemsByType;

    const [loadRiskCategories, { data: riskCategoriesData }] = useLazyQuery<
        getNamedItemsByType,
        getNamedItemsByTypeVariables
    >(GET_NAMED_ITEMS_BY_TYPE);

    const riskCategories = riskCategoriesData?.namedItemsByType;

    const [loadControlPhases, { data: controlPhasesData }] = useLazyQuery<
        getNamedItemsByType,
        getNamedItemsByTypeVariables
    >(GET_NAMED_ITEMS_BY_TYPE);

    const controlPhases = controlPhasesData?.namedItemsByType;

    const [loadControlTypes, { data: controlTypesData }] = useLazyQuery<
        getNamedItemsByType,
        getNamedItemsByTypeVariables
    >(GET_NAMED_ITEMS_BY_TYPE);

    const controlTypes = controlTypesData?.namedItemsByType;

    const [loadResponsibleFunctions, { data: responsibleFunctionsData }] = useLazyQuery<
        getNamedItemsByType,
        getNamedItemsByTypeVariables
    >(GET_NAMED_ITEMS_BY_TYPE);

    const [loadVendors, { data: vendorsData }] = useLazyQuery<getNamedItemsByType, getNamedItemsByTypeVariables>(
        GET_NAMED_ITEMS_BY_TYPE,
    );

    const vendors = vendorsData?.namedItemsByType;

    const responsibleFunctions = responsibleFunctionsData?.namedItemsByType;

    const [loadControlStrategies, { data: controlStrategiesData }] = useLazyQuery<
        getNamedItemsByType,
        getNamedItemsByTypeVariables
    >(GET_NAMED_ITEMS_BY_TYPE);

    const controlStrategies = controlStrategiesData?.namedItemsByType;

    useEffect(() => {
        const organisationId = location.pathname.match(AllRoutes.path('org-risk-library'))
            ? (state.selectedOrganisation?.id ?? user.organisationId)
            : null;

        setOrganisationId(organisationId ?? Constants.ObjectId.Empty);
    }, [state.selectedOrganisation, user, location.pathname]);

    const getProcessedLibraryRisks = (
        risks: getLibraryRisks_OpraQueries_libraryRisks_LibraryRisk[],
        isParent: boolean,
    ): LibraryRiskFieldsExtended[] => {
        if (!risks) return [];

        return risks.map((risk) => {
            const associatedControls = isParent
                ? getProcessedLibraryControls(
                      libraryControls?.filter(
                          (control: LibraryControlFieldsExtended) => risk.libraryControlIds.indexOf(control.id) >= 0,
                      ),
                      false,
                  )
                : [];
            const numberOfControls = isParent ? (associatedControls.length ?? 0) : 0;
            const libraryCategories =
                riskLibraryCategories?.filter((el) => risk.riskLibraryCategoryIds.indexOf(el.id) >= 0) ?? [];
            const riskLibraryCategory = libraryCategories ? libraryCategories.map((el) => el.name).join(', ') : '';
            const country = risk.countries ? risk.countries.map((el) => el.name).join(', ') : '';
            const vendor = risk.vendors ? risk.vendors?.map((el) => el?.name).join(', ') : '';
            const countries = risk.countries?.filter(notEmpty).map(({ code, name }) => ({ code, name })) ?? [];
            return {
                ...risk,
                associatedControls,
                numberOfControls,
                riskLibraryCategory,
                country,
                countries,
                vendor,
                riskLibraryCategories: libraryCategories,
                _reviewFrequency: convertReviewFrequency(risk.reviewFrequencyPeriod, risk.reviewFrequencyUnits),
                _reviewFrequencyMS: convertDateToDays(risk.reviewFrequencyPeriod, risk.reviewFrequencyUnits),
            };
        });
    };

    const getProcessedLibraryControls = (
        controls: getLibraryRisks_OpraQueries_libraryRisks_LibraryRisk_libraryControls_LibraryControl[],
        isParent: boolean,
    ): LibraryControlFieldsExtended[] => {
        if (!controls) return [];

        return controls.map(
            (control: getLibraryRisks_OpraQueries_libraryRisks_LibraryRisk_libraryControls_LibraryControl) => {
                const associatedRisks = isParent
                    ? getProcessedLibraryRisks(
                          libraryRisks?.filter(
                              (risk: getLibraryRisks_OpraQueries_libraryRisks_LibraryRisk) =>
                                  risk.libraryControlIds.indexOf(control.id) >= 0,
                          ),
                          false,
                      )
                    : [];
                const originLibrary = isParent
                    ? systemLibraryControlsData?.libraryControls.find(
                          (el: getLibraryRisks_OpraQueries_libraryRisks_LibraryRisk_libraryControls_LibraryControl) =>
                              el.id === control.systemReferenceId,
                      )
                    : undefined;
                const associatedSystemRisks = isParent
                    ? (systemLibraryRisksData?.libraryRisks
                          ?.filter(
                              (risk: getLibraryRisks_OpraQueries_libraryRisks_LibraryRisk) =>
                                  risk.libraryControlIds.indexOf(originLibrary?.id) >= 0,
                          )
                          .map((risk: getLibraryRisks_OpraQueries_libraryRisks_LibraryRisk) => risk.id) ?? [])
                    : [];
                const numberOfRisks = isParent ? associatedRisks.length : 0;

                return {
                    ...control,
                    associatedRisks,
                    associatedSystemRisks,
                    numberOfRisks,
                    _reviewFrequency: convertReviewFrequency(
                        control.reviewFrequencyPeriod,
                        control.reviewFrequencyUnits,
                    ),
                    _reviewFrequencyMS: convertDateToDays(control.reviewFrequencyPeriod, control.reviewFrequencyUnits),
                };
            },
        );
    };

    useEffect(() => {
        if (libraryRisks) {
            setProcessedLibraryRisks(getProcessedLibraryRisks(libraryRisks, true));
        }
        if (libraryControls) {
            const array = getProcessedLibraryControls(libraryControls, true);
            setProcessedLibraryControls(array);
        }
    }, [
        libraryControls,
        libraryRisks,
        systemLibraryRisksData,
        systemLibraryControlsData,
        riskLibraryCategories,
        riskCategories,
        responsibleFunctions,
        controlTypes,
    ]);

    useEffect(() => {
        if (organisationId) {
            loadSystemRisks({
                variables: {
                    organisationId: Constants.ObjectId.Empty,
                    includeSystemRisks: true,
                },
            });
            loadSystemControls({
                variables: {
                    organisationId: Constants.ObjectId.Empty,
                    includeSystemControls: true,
                },
            });
            loadLibraryFunctionalDataReviews();
            loadRiskLibraryCategories({
                variables: {
                    type: NamedItemType.RISK_LIBRARY_CATEGORY,
                },
            });
            loadRiskCategories({
                variables: {
                    type: NamedItemType.RISK_CATEGORY,
                },
            });
            loadControlPhases({
                variables: {
                    type: NamedItemType.CONTROL_PHASE,
                },
            });
            loadControlTypes({
                variables: {
                    type: NamedItemType.CONTROL_TYPE,
                },
            });
            loadResponsibleFunctions({
                variables: {
                    type: NamedItemType.RESPONSIBLE_FUNCTION,
                },
            });
            loadControlStrategies({
                variables: {
                    type: NamedItemType.RISK_CONTROL_STRATEGY,
                },
            });
            loadVendors({
                variables: {
                    type: NamedItemType.VENDOR,
                },
            });
        }
    }, [organisationId]);

    const showError = () => {
        NotificationService.showNotification(t('errorMessage'));
    };

    const saveRisk = (risk?: LibraryRiskFieldsExtended) => {
        let value = risk || currentRisk;
        if (value?.library) {
            value = {
                ...value,
                id: null,
                systemReferenceId: value.id,
                organisationId,
            };
            delete value.library;
        }
        return saveLibraryRisk({
            variables: {
                risk: {
                    ...value,
                },
            },
            refetchQueries: [
                {
                    query: GET_LIBRARY_RISKS,
                    variables: {
                        organisationId,
                        includeSystemRisks: isSystemLevel,
                    },
                },
                {
                    query: GET_LIBRARY_CONTROLS,
                    variables: {
                        organisationId,
                        includeSystemControls: isSystemLevel,
                    },
                },
            ],
        });
    };

    const saveControl = (
        control?: getLibraryRisks_OpraQueries_libraryRisks_LibraryRisk_libraryControls_LibraryControl,
    ) =>
        saveLibraryControl({
            variables: {
                control: {
                    ...control,
                },
            },
            refetchQueries: [
                {
                    query: GET_LIBRARY_CONTROLS,
                    variables: {
                        organisationId,
                        includeSystemControls: isSystemLevel,
                    },
                },
            ],
        });

    const assignRisksToControl = (
        selectedItems: LibraryRiskFieldsExtended[],
        closeWindow: boolean,
        notificationText?: string,
    ) => {
        onLoadingAssignRisks.setTrue();
        selectedItems = selectedItems.map((element) => {
            let value: any = pick(element as any, libraryRiskUpdate);
            const ids: any[] | null = element.libraryControlIds;
            value = {
                ...value,
                libraryControlIds:
                    element.library && !isSystemLevel ? [currentControl?.id] : ids?.concat(currentControl?.id),
                organisationId,
                id: element.library && !isSystemLevel ? null : value.id,
                systemReferenceId: element.library && !isSystemLevel ? value.id : Constants.ObjectId.Empty,
                countries: element.countries?.filter((c) => c !== null).map(({ code, name }) => ({ code, name })) ?? [],
            };
            return value;
        });
        saveLibraryRisks({
            variables: {
                risks: selectedItems,
            },
            refetchQueries: [
                {
                    query: GET_LIBRARY_RISKS,
                    variables: {
                        organisationId,
                        includeSystemRisks: isSystemLevel,
                    },
                },
                {
                    query: GET_LIBRARY_CONTROLS,
                    variables: {
                        organisationId,
                        includeSystemControls: isSystemLevel,
                    },
                },
            ],
        })
            .then(() => {
                if (currentControl)
                    setCurrentControl(
                        processedLibraryControls.find(
                            (
                                item: getLibraryRisks_OpraQueries_libraryRisks_LibraryRisk_libraryControls_LibraryControl,
                            ) => item.id === currentControl.id,
                        ) || null,
                    );
                NotificationService.showNotification(
                    notificationText ||
                        t('ra:successfulAssignedRisksToControl', {
                            amount: selectedItems.length,
                            id: currentControl?.recordId || currentControl?.id,
                        }),
                );
                if (closeWindow) {
                    showAddLibraryRiskModal.setFalse();
                    showAssignRisks.setFalse();
                    setCurrentControl(null);
                }
                onLoadingAssignRisks.setFalse();
            })
            .catch((e: any) => {
                console.log('error', e);
                showError();
                onLoadingAssignRisks.setFalse();
            });
    };

    const addRisks = (selectedItems: LibraryRiskFieldsExtended[], closeWindow: boolean) => {
        onLoadingAssignRisks.setTrue();
        selectedItems = selectedItems.map((element: LibraryRiskFieldsExtended) => {
            let value: any = pick(element, libraryRiskUpdate);
            value = {
                ...value,
                id: null,
                libraryControlIds: [],
                systemReferenceId: element.id,
                organisationId,
                active: true,
            };
            return value;
        });
        saveLibraryRisks({
            variables: {
                risks: selectedItems,
            },
            refetchQueries: [
                {
                    query: GET_LIBRARY_RISKS,
                    variables: {
                        organisationId,
                        includeSystemRisks: isSystemLevel,
                    },
                },
                {
                    query: GET_LIBRARY_CONTROLS,
                    variables: {
                        organisationId,
                        includeSystemControls: isSystemLevel,
                    },
                },
            ],
        })
            .then(() => {
                NotificationService.showNotification(
                    t('ra:successfulCreateRiskLibraryAmount', { amount: selectedItems.length }),
                );
                if (closeWindow) showAddRisks.setFalse();
                onLoadingAssignRisks.setFalse();
            })
            .catch((e: any) => {
                showError();
                console.log('error', e);
                onLoadingAssignRisks.setFalse();
            });
    };

    const saveAssignControlsToRisk = (
        selectedItems: LibraryControlFieldsExtended[],
        closeWindow: boolean,
        notificationMessage?: string,
    ) => {
        let value: any = pick(currentRisk, libraryRiskUpdate);
        const ids = currentRisk?.libraryControlIds || [];
        value = {
            ...value,
            libraryControlIds: ids.concat(selectedItems.map((item: LibraryControlFieldsExtended) => item.id)),
            organisationId,
        };
        saveRisk(value)
            .then((result: any) => {
                setCurrentRisk(result.data?.updateLibraryRisk);
                NotificationService.showNotification(
                    notificationMessage ||
                        `${t('ra:libraryControlsHaveBeenSuccessfullyAssignedRisk', {
                            cvId: currentRisk?.recordId,
                            controlNumber: selectedItems.length,
                        })}`,
                );
                setCurrentControl(null);
                if (closeWindow) {
                    setCurrentRisk(null);
                    showAssignControls.setFalse();
                    showAddLibraryControl.setFalse();
                }
                loadingAssignControls.setFalse();
            })
            .catch((e: any) => {
                showError();
                console.log('error', e);
                loadingAssignControls.setFalse();
            });
    };

    const assignControlToRisk = (
        selectedItems: LibraryControlFieldsExtended[],
        closeWindow: boolean,
        notificationMessage?: string,
    ) => {
        let systemControls: LibraryControlFieldsExtended[] = [];
        if (!isSystemLevel)
            systemControls = selectedItems?.filter((item: LibraryControlFieldsExtended) => item.library);
        loadingAssignControls.setTrue();
        if (systemControls.length) {
            const array = systemControls.map(
                (item: getLibraryRisks_OpraQueries_libraryRisks_LibraryRisk_libraryControls_LibraryControl) => ({
                    ...pick(item, libraryControlUpdate),
                    id: null,
                    systemReferenceId: item.id,
                    organisationId,
                }),
            );
            saveLibraryControls({
                variables: {
                    controls: array,
                },
            }).then((result) => {
                selectedItems = selectedItems?.filter((item: LibraryControlFieldsExtended) => !item.library) || [];
                selectedItems.push(...result.data.updateLibraryControls);
                saveAssignControlsToRisk(selectedItems, closeWindow, notificationMessage);
            });
        } else {
            saveAssignControlsToRisk(selectedItems, closeWindow, notificationMessage);
        }
    };

    const addControls = (selectedItems: LibraryControlFieldsExtended[], closeWindow: boolean) => {
        loadingAssignControls.setTrue();
        const promiseArray: any = [];
        selectedItems.forEach((element: LibraryControlFieldsExtended) => {
            let value: any = pick(element, libraryControlUpdate);
            value = {
                ...value,
                id: null,
                systemReferenceId: element.id,
                organisationId,
                active: true,
            };
            promiseArray.push(saveControl(value));
        });
        Promise.all(promiseArray)
            .then(() => {
                NotificationService.showNotification(
                    t('ra:successfulCreateControlLibraryAmount', { amount: selectedItems.length }),
                );
                if (closeWindow) showAddControls.setFalse();
                loadingAssignControls.setFalse();
            })
            .catch((e: any) => {
                showError();
                console.log('error', e);
                loadingAssignControls.setFalse();
            });
    };

    const deleteControl = () => {
        deleteLibraryControl({
            variables: { id: currentControl?.id },
            refetchQueries: [
                {
                    query: GET_LIBRARY_RISKS,
                    variables: {
                        organisationId,
                        includeSystemRisks: isSystemLevel,
                    },
                },
                {
                    query: GET_LIBRARY_CONTROLS,
                    variables: {
                        organisationId,
                        includeSystemControls: isSystemLevel,
                    },
                },
            ],
        })
            .then(() => {
                showDeleteItem.setFalse();
                NotificationService.showNotification(t('admin:deleteControlSuccessMessage'));
                setCurrentControl(null);
            })
            .catch(() => showError());
    };

    const deleteRisk = () => {
        deleteLibraryRisk({
            variables: { id: currentRisk?.id },
            refetchQueries: [
                {
                    query: GET_LIBRARY_RISKS,
                    variables: {
                        organisationId,
                        includeSystemRisks: isSystemLevel,
                    },
                },
                {
                    query: GET_LIBRARY_CONTROLS,
                    variables: {
                        organisationId,
                        includeSystemControls: isSystemLevel,
                    },
                },
            ],
        })
            .then(() => {
                showDeleteItem.setFalse();
                setCurrentRisk(null);
                NotificationService.showNotification(t('admin:deleteRiskSuccessMessage'));
            })
            .catch(() => showError());
    };

    const onUnassignControlClose = (action: string | React.MouseEvent) => {
        if (action === 'error') {
            showError();
        } else {
            if ((showUnassignControlFromRisk.value || showUnassignRiskFromControl.value) && action === 'success') {
                const notificationTitle = showUnassignControlFromRisk.value
                    ? 'ra:successUnassignLibraryControlFromRisk'
                    : 'ra:successUnassignLibraryRiskFromControl';

                NotificationService.showNotification(
                    t(notificationTitle, {
                        controlId: currentControl?.recordId || currentControl?.id,
                        riskId: currentRisk?.recordId || currentRisk?.id,
                    }),
                );
            }

            showUnassignControlFromRisk.setFalse();
            showUnassignRiskFromControl.setFalse();
            setCurrentRisk(null);
            setCurrentControl(null);
        }
    };

    const openAddItemDialog = () => {
        if (isSystemLevel) {
            if (isRiskTab) {
                showAddLibraryRiskModal.setTrue();
            } else {
                showAddLibraryControl.setTrue();
            }
        } else if (isRiskTab) {
            showAddRisks.setTrue();
        } else {
            showAddControls.setTrue();
        }
    };

    const risksRowActions = (risk: LibraryRiskFieldsExtended) => [
        {
            label: 'ra:editRisk',
            icon: ['fal', 'pen-to-square'],
            enabled: canEdit,
            onClick: () => {
                setCurrentRisk(risk);
                showAddLibraryRiskModal.setTrue();
            },
        },
        {
            label: 'ra:assignControls',
            enabled: canEdit,
            icon: ['fal', 'link'],
            onClick: () => {
                setCurrentRisk(risk);
                showAssignControls.setTrue();
            },
        },
        {
            label: 'ra:deleteRisk',
            icon: ['fal', 'trash'],
            onClick: () => {
                setCurrentRisk(risk);
                showDeleteItem.setTrue();
            },
            enabled: canEdit,
        },
    ];

    const controlsRowActions = (item: LibraryControlFieldsExtended) => [
        {
            label: 'ra:editControlLabel',
            icon: ['fal', 'pen-to-square'],
            onClick: () => {
                setCurrentControl(item);
                showAddLibraryControl.setTrue();
            },
            enabled: canEdit,
        },
        {
            label: 'ra:assignRisks',
            icon: ['fal', 'link'],
            onClick: () => {
                setCurrentControl(item);
                showAssignRisks.setTrue();
            },
            enabled: canEdit,
        },
        {
            label: 'ra:deleteControlLabel',
            icon: ['fal', 'trash'],
            onClick: () => {
                setCurrentControl(item);
                showDeleteItem.setTrue();
            },
            enabled: canEdit,
        },
    ];

    const functionalDataReviewActions = (
        item: libraryFunctionalDataReviews_OpraQueries_libraryFunctionalDataReviews_LibraryFunctionalDataReview,
    ) => [
        {
            label: 'common:edit',
            icon: ['fal', 'pen-to-square'],
            enabled: canEdit,
            onClick: () => {
                setCurrentFunctionalDataReview(item);
                showAddFunctionalDataReviewDialog.setTrue();
            },
        },
    ];

    const onRiskAssign = (item: LibraryRiskFieldsExtended) => {
        assignRisksToControl(
            [item],
            true,
            t('ra:successfulCreateRiskAssignedToLib', { controlId: currentControl?.recordId }),
        );
    };

    const onControlAssign = (item: LibraryControlFieldsExtended) => {
        assignControlToRisk(
            [item],
            true,
            t('ra:libraryControlHasBeenSuccessfullyCreatedAndAssignRisk', { riskId: currentRisk?.recordId }),
        );
    };

    const onCloseAddItemLib = (action: string | boolean | undefined, isRisk?: boolean) => {
        if (action === 'error') {
            NotificationService.showNotification(t('errorMessage'));
        } else {
            if (action === 'add') {
                NotificationService.showNotification(
                    t(isRisk ? 'ra:successfulCreateRiskLibrary' : 'ra:successfulCreateControlLibrary'),
                );
            } else if (action === 'edit') {
                NotificationService.showNotification(
                    isRisk
                        ? t('ra:successfulUpdateRiskLibrary', { riskId: currentRisk?.recordId || currentRisk?.id })
                        : t('ra:successfulUpdateControlLibrary', {
                              id: currentControl?.recordId || currentControl?.id,
                          }),
                );
            }

            setCurrentRisk(null);
            setCurrentControl(null);

            if (isRisk) {
                if (showAddRisks.value) {
                    showAddRisks.setFalse();
                }
                showAddLibraryRiskModal.setFalse();
            } else {
                if (showAddControls.value) {
                    showAddControls.setFalse();
                }
                showAddLibraryControl.setFalse();
            }
        }
    };

    const onCloseRisksControlLib = (action: string | boolean | undefined) => {
        if (action === 'open-add') {
            showAddLibraryRiskModal.setTrue();
            showAssignRisks.setFalse();
            showAddRisks.setFalse();
            return;
        }

        setCurrentControl(null);
        setCurrentRisk(null);
        showAssignRisks.setFalse();
        showAddRisks.setFalse();
    };

    const onCloseControlsRiskLib = (action: string | boolean | undefined) => {
        if (action === 'open-add') {
            showAddLibraryControl.setTrue();
            showAssignControls.setFalse();
        } else {
            setCurrentRisk(null);
            showAssignControls.setFalse();
            showAddControls.setFalse();
        }
    };

    const onAssignControlsRisk = (closeWindow: boolean, selectedItems: LibraryControlFieldsExtended[]) => {
        if (showAssignControls.value) {
            assignControlToRisk(selectedItems, closeWindow);
        } else if (showAddControls.value) {
            addControls(selectedItems, closeWindow);
        }
    };

    const onAssignRisksControl = (closeWindow: boolean, selectedItems: LibraryRiskFieldsExtended[]) => {
        if (showAssignRisks.value) {
            assignRisksToControl(selectedItems, closeWindow);
        } else if (showAddRisks.value) {
            addRisks(selectedItems, closeWindow);
        }
    };

    const onOpenRisksControlLib = () => {
        if (showAssignRisks.value) {
            showAssignRisks.setFalse();
        }
        if (showAddRisks.value) {
            showAddRisks.setFalse();
        }

        showAddLibraryRiskModal.setTrue();
    };

    const onOpenControlsRiskLib = () => {
        if (showAssignControls.value) {
            showAssignControls.setFalse();
        }
        if (showAddControls.value) {
            showAddControls.setFalse();
        }

        showAddLibraryControl.setTrue();
    };

    const onEditControl = (item: LibraryControlFieldsExtended) => {
        const associatedRisks = getProcessedLibraryRisks(
            libraryRisks?.filter((risk) => risk.libraryControlIds.indexOf(item.id) >= 0),
            false,
        );
        setCurrentControl({ ...item, associatedRisks });
        showAddLibraryControl.setTrue();
    };

    const onEditRisk = (item: LibraryRiskFieldsExtended) => {
        const associatedControls = getProcessedLibraryControls(
            libraryControls?.filter((control) => item.libraryControlIds.indexOf(control.id) >= 0),
            false,
        );

        setCurrentRisk({ ...item, associatedControls });
        showAddLibraryRiskModal.setTrue();
    };

    const onUnassignItem = (risk: LibraryRiskFieldsExtended, control: LibraryControlFieldsExtended) => {
        setCurrentRisk(risk);
        setCurrentControl(control);

        if (isRiskTab) {
            showUnassignControlFromRisk.setTrue();
        } else {
            showUnassignRiskFromControl.setTrue();
        }
    };

    const onSubmitRiskOrderDialog = () => {
        if (showReorderGrid.value) {
            showReorderGrid.setFalse();
        } else {
            showReorderGrid.setTrue();
        }
        showEditRiskOrderDialog.setFalse();
    };

    const refetchRiskData = () => {
        loadSystemRisks({
            variables: {
                organisationId,
                includeSystemRisks: isSystemLevel,
            },
        });
    };

    const getPageTitle = () => {
        if (!isSystemLevel) return 'admin:organisationRiskLibrary';

        return 'admin:systemRiskLibrary';
    };

    if (
        libraryRisksLoading ||
        libraryControlsLoading ||
        loadingLibraryRisks ||
        loadingLibraryControls ||
        loadingLibraryFunctionalDataReviews
    )
        return <Loading float />;

    if (libraryRisksError) return <Error error={libraryRisksError} />;

    const filteredSystemLibraryRisks = systemLibraryRisksData?.libraryRisks?.filter(
        (item) =>
            processedLibraryRisks?.findIndex(
                (el: getLibraryRisks_OpraQueries_libraryRisks_LibraryRisk) => el.systemReferenceId === item?.id,
            ) < 0,
    );

    const filteredSystemLibraryControls = systemLibraryControlsData?.libraryControls?.filter(
        (item) =>
            processedLibraryControls?.findIndex(
                (el: getLibraryRisks_OpraQueries_libraryRisks_LibraryRisk_libraryControls_LibraryControl) =>
                    el.systemReferenceId === item?.id,
            ) < 0,
    );

    return (
        <>
            <UnassignLibraryControl
                open={showUnassignControlFromRisk.value || showUnassignRiskFromControl.value}
                onClose={onUnassignControlClose}
                isRisk={showUnassignRiskFromControl.value}
                currentControl={currentControl}
                currentRisk={currentRisk}
                organisationId={organisationId}
            />
            {user?.systemAdminLevel > 0 && importDialogOpen.value && (
                <RisksAndControlsImportDialog
                    isRisk={active === 0}
                    organisationId={organisationId}
                    importDialogOpen={importDialogOpen}
                />
            )}
            <AddLibraryRisk
                open={showAddLibraryRiskModal.value}
                onAssign={onRiskAssign}
                onClose={onCloseAddItemLib}
                riskLibraryCategories={riskLibraryCategories}
                riskCategories={riskCategories}
                currentControl={currentControl}
                canEdit
                organisationId={organisationId}
                risk={currentRisk}
                responsibleFunctions={responsibleFunctions}
                isSystemLevel={isSystemLevel}
                controlStrategies={controlStrategies}
                vendors={vendors}
            />
            <AddLibraryControl
                open={showAddLibraryControl.value}
                isSystemLevel={isSystemLevel}
                onAssign={onControlAssign}
                onClose={onCloseAddItemLib}
                control={currentControl}
                currentRisk={currentRisk}
                organisationId={organisationId}
                controlPhases={controlPhases}
                controlTypes={controlTypes}
                responsibleFunctions={responsibleFunctions}
            />
            <AddLibraryRisksControl
                onLoading={onLoadingAssignRisks.value}
                open={showAssignRisks.value || showAddRisks.value}
                showNew
                isSystem={isSystemLevel}
                showAddRisks={showAddRisks.value}
                onClose={onCloseRisksControlLib}
                libraryRisks={
                    (showAssignRisks.value && !isSystemLevel) || showAddRisks.value
                        ? filteredSystemLibraryRisks
                        : processedLibraryRisks
                }
                risks={!isSystemLevel && showAssignRisks.value ? processedLibraryRisks : []}
                currentControl={currentControl}
                onAssign={onAssignRisksControl}
                openNew={onOpenRisksControlLib}
            />
            <AddLibraryControlsRisk
                open={showAssignControls.value || showAddControls.value}
                showNew
                isSystem={isSystemLevel}
                showAddControls={showAddControls.value}
                onLoading={loadingAssignControls.value}
                onClose={onCloseControlsRiskLib}
                onAssign={onAssignControlsRisk}
                libraryControls={
                    (showAssignControls.value && !isSystemLevel) || showAddControls.value
                        ? filteredSystemLibraryControls
                        : processedLibraryControls
                }
                controls={showAssignControls.value && !isSystemLevel ? processedLibraryControls : []}
                currentRisk={currentRisk}
                systemRisks={systemLibraryRisksData?.libraryRisks}
                openNew={onOpenControlsRiskLib}
            />
            {showDeleteItem.value && (
                <ActionDialog
                    title={t(isRiskTab ? 'admin:deleteRiskDialogHeading' : 'admin:deleteControlDialogHeading', {
                        itemId: isRiskTab ? currentRisk?.recordId : currentControl?.recordId,
                    })}
                    onClose={() => {
                        setCurrentRisk(null);
                        setCurrentControl(null);
                        showDeleteItem.setFalse();
                    }}
                    onSubmit={isRiskTab ? deleteRisk : deleteControl}
                    submitText={t('delete')}
                    className="action-dialog-small"
                >
                    <div>
                        {t(isRiskTab ? 'admin:deleteRiskDialogText' : 'admin:deleteControlDialogText', {
                            itemId: isRiskTab ? currentRisk?.recordId : currentControl?.recordId,
                        })}
                    </div>
                </ActionDialog>
            )}
            {showEditRiskOrderDialog.value && (
                <ActionDialog
                    title={t('ra:editRiskOrderDialogHeading')}
                    onClose={() => showEditRiskOrderDialog.setFalse()}
                    onSubmit={onSubmitRiskOrderDialog}
                    submitText={t('continue')}
                    className="action-dialog-small"
                >
                    <div>{t('ra:reorderDialogWarningMessage')}</div>
                </ActionDialog>
            )}
            {showAddFunctionalDataReviewDialog.value && (
                <AddFunctionalDataReviewDialog
                    onSubmit={(result: string | undefined) => {
                        showAddFunctionalDataReviewDialog.setFalse();
                        setCurrentFunctionalDataReview(null);
                        NotificationService.showNotification(result);
                    }}
                    onClose={() => {
                        setCurrentFunctionalDataReview(null);
                        showAddFunctionalDataReviewDialog.setFalse();
                    }}
                    functionalDataReview={currentFunctionalDataReview}
                    responsibleFunctions={responsibleFunctions}
                />
            )}
            <PageTitle text={t(getPageTitle())} />
            {showReorderGrid.value ? (
                <RisksReorderingGrid
                    items={updateRisksReorderingGridData(processedLibraryRisks)}
                    columns={GET_ADMIN_RISK_REORDERING_GRID_COLUMNS(t)}
                    refetchData={refetchRiskData}
                    onSaveReorder={showReorderGrid.setFalse}
                    closeReorder={showReorderGrid.setFalse}
                />
            ) : (
                <>
                    <Tabs value={active} onChange={(e: any, value: any) => setActive(value)}>
                        <Tab style={{ fontSize: 16, minWidth: 50 }} label={t('ra:risks')} data-testid="risks-tab" />
                        <Tab
                            style={{ fontSize: 16, minWidth: 50 }}
                            label={t('ra:controls')}
                            data-testid="controls-tab"
                        />
                        {organisationId !== Constants.ObjectId.Empty && (
                            <Tab
                                style={{ fontSize: 16, minWidth: 50 }}
                                label={t('ra:functionalDataReviews')}
                                data-testid="functionalDataReviews-tab"
                            />
                        )}
                    </Tabs>

                    <br />
                    {(active === 0 || active === 1) && (
                        <AddItemsBtns
                            isSystemAdminLevel={!!user}
                            canEdit={!!canEdit}
                            importBtnText={t(isRiskTab ? 'ra:importRisks' : 'ra:importControls')}
                            importBtnClick={() => importDialogOpen.setTrue()}
                            addBtnText={t(isRiskTab ? 'ra:addRisks' : 'ra:addControls')}
                            addBtnClick={openAddItemDialog}
                        />
                    )}
                    {active === 2 && (
                        <div className="top-title-btn-wrapper no-mt">
                            <Button
                                onClick={() => {
                                    showAddFunctionalDataReviewDialog.setTrue();
                                }}
                                disabled={!canEdit}
                                className="main-button"
                            >
                                {t('ra:addFunctionalDataReview')}
                            </Button>
                        </div>
                    )}
                    {active === 0 && (
                        <AdminRisksGrid
                            className="risk-library-admin-grid-scroll"
                            title={t('ra:risks')}
                            data={processedLibraryRisks}
                            showPager
                            showDetail
                            canEdit={canEdit}
                            isSystemLevel={isSystemLevel}
                            editControl={onEditControl}
                            unassignControl={onUnassignItem}
                            rowActions={risksRowActions}
                            extraMenuItems={
                                !isSystemLevel
                                    ? [
                                          {
                                              position: ExtraMenuItemPosition.left,
                                              tooltipTitle: t('ra:reorderRisks'),
                                              onClick: showEditRiskOrderDialog.setTrue,
                                              icon: ['fal', 'arrows-up-down-left-right'],
                                          },
                                      ]
                                    : []
                            }
                        />
                    )}
                    {active === 1 && (
                        <AdminControlsGrid
                            className="risk-library-admin-grid-scroll"
                            items={processedLibraryControls}
                            isSystemLevel={isSystemLevel}
                            canEdit={canEdit}
                            editRisk={onEditRisk}
                            unassignRisk={onUnassignItem}
                            rowActions={controlsRowActions}
                        />
                    )}
                    {active === 2 && (
                        <FunctionalDataReviewGrid
                            AdminFunctionalDataReviewGrid
                            className="functional-data-review-admin-grid-scroll"
                            items={LibraryFunctionalDataReviewsData}
                            rowActions={functionalDataReviewActions}
                        />
                    )}
                </>
            )}
        </>
    );
};

export default AdminRisks;
